import { useCallback, useEffect, useState } from 'react';
import { syncUrlToView, viewKeyFromLegacyQuery, viewKeyFromPath } from '../lib/routes.js';

/**
 * Work item #11 — écran actif adossé à l'URL, sans router.
 *
 * Remplace le `useState(?view=…)` d'`App.jsx` : l'état initial est lu dans le
 * pathname (donc un CTRL+R rouvre le même écran), chaque navigation pousse une
 * entrée d'historique (donc Précédent/Suivant du navigateur fonctionnent), et
 * `popstate` remet l'état en phase.
 *
 * `knownKeys` doit être le set des vues *visibles par l'utilisateur*, pas le
 * catalogue complet : un manager qui ouvre `/prospection` doit atterrir sur la
 * vue de repli avec une URL réécrite en conséquence, pas sur une URL menteuse.
 *
 * @param {Set<string>|Map<string, unknown>} knownKeys clés de vue autorisées
 * @param {string} fallbackKey vue de repli si l'URL ne désigne rien de connu
 * @returns {{ activeView: string, navigate: (key: string, opts?: {replace?: boolean}) => void }}
 */
export function useViewRoute(knownKeys, fallbackKey = 'forecast') {
  const [activeView, setActiveView] = useState(
    () =>
      viewKeyFromPath(window.location.pathname, knownKeys) ??
      viewKeyFromLegacyQuery(window.location.search, knownKeys) ??
      fallbackKey,
  );

  // Racine `/`, URL exotique, vue interdite par les droits, ou ancien lien
  // `?view=` : on réécrit l'URL sur la vue réellement affichée, en `replace`
  // pour ne pas créer une entrée d'historique fantôme.
  useEffect(() => {
    syncUrlToView(activeView, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Les droits arrivent après le premier rendu (/auth/me) : si la vue courante
  // sort du périmètre autorisé, on retombe sur le repli et on aligne l'URL.
  useEffect(() => {
    if (knownKeys.has(activeView)) return;
    setActiveView(fallbackKey);
    syncUrlToView(fallbackKey, { replace: true });
  }, [knownKeys, activeView, fallbackKey]);

  useEffect(() => {
    const onPopState = () => {
      setActiveView(viewKeyFromPath(window.location.pathname, knownKeys) ?? fallbackKey);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [knownKeys, fallbackKey]);

  const navigate = useCallback((key, { replace = false } = {}) => {
    setActiveView(key);
    syncUrlToView(key, { replace });
  }, []);

  return { activeView, navigate };
}
